import React, { useCallback, useMemo } from 'react';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Link } from 'react-router-dom';
import {
  AutomationsFieldDescription,
  AutomationsFieldLabel,
  AutomationsFieldContainer,
  AutomationsTagBlock,
} from 'pages/Automations/components/Layout';
import { useGetAutomationWidgetRequestTypeOptionsQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import useClinicUser from 'shared/hooks/useClinicUser';
import { updateAutomationErrors, updateAutomationDraft } from '../../../state/automationsSlice';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';
import sortBy from 'lodash-es/sortBy';
import { TagDisabledState } from '../../Layout/AutomationsTagBlock';
import { WidgetRequestTypeLabels } from '@televet/shared-types/widgetRequest/WidgetRequestTypeLabels';

export const RequestOptions = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const dispatch = useAppDispatch();
  const requestOptions = useAppSelector((state) => state.automations.automationDraft.requestOptions);
  const fieldErrors = useAppSelector((state) => state.automations.errors.requestTypes);
  const errorText = useGetFieldErrorText(fieldErrors);

  const { data: requestTypesData } = useGetAutomationWidgetRequestTypeOptionsQuery({
    variables: {
      where: {
        id: currentClinicId,
      },
    },
    skip: !currentClinicId,
  });

  const handleRemoveTag = useCallback(
    (tagId: string) => {
      dispatch(updateAutomationDraft({ requestOptions: requestOptions?.filter((id) => id !== tagId) }));
    },
    [dispatch, requestOptions],
  );

  const handleAddTag = useCallback(
    (allSelectedItems: MenuItemProps[] | undefined) => {
      if (allSelectedItems) {
        const updates = { requestOptions: allSelectedItems.map(({ value }) => value || '') };
        dispatch(updateAutomationDraft(updates));

        if (Boolean(fieldErrors.length)) {
          dispatch(updateAutomationErrors({ requestTypes: [] }));
        }
      }
    },
    [dispatch, fieldErrors],
  );

  const handleClearAllTags = useCallback(() => {
    dispatch(updateAutomationDraft({ requestOptions: [] }));
  }, [dispatch]);

  const tagBlockRequestTypeOptions = useMemo(() => {
    const options = requestTypesData?.findUniqueClinic?.clinicWidgetSetting?.clinicWidgetRequestTypes.map(
      ({ id, displayName, requestType, isDeleted, isActive }) => ({
        label: displayName || WidgetRequestTypeLabels[requestType],
        value: id,
        isSelected: requestOptions?.includes(id),
        isActive: !!isActive,
        isDeleted: !!isDeleted,
      }),
    );

    return sortBy(options, ['label']) || [];
  }, [requestTypesData, requestOptions]);

  const selectedRequestTypes = useMemo(
    () =>
      tagBlockRequestTypeOptions
        ?.filter(({ isSelected }) => isSelected)
        ?.map(({ label, value, isActive, isDeleted }) => ({
          label: `${label}${
            isDeleted ? ` (${TagDisabledState.Deleted})` : !isActive ? ` (${TagDisabledState.Inactive})` : ''
          }`,
          value: value || '',
        })) || [],
    [tagBlockRequestTypeOptions],
  );

  return (
    <AutomationsFieldContainer>
      <AutomationsFieldLabel>Request Options</AutomationsFieldLabel>
      <AutomationsFieldDescription>
        This automation will trigger when the following website widget request options are submitted. Request Options
        can be edited in{' '}
        <Link to="/settings/clinic/widget">
          <Text variant="interactive" textDecoration="underline">
            Settings
          </Text>
        </Link>
        .
      </AutomationsFieldDescription>
      <AutomationsTagBlock
        tags={selectedRequestTypes}
        onRemoveTag={handleRemoveTag}
        onClearAll={handleClearAllTags}
        errorText={errorText}
        hasError={Boolean(fieldErrors.length)}
        listProps={{
          isMultiSelect: true,
          isSearchable: true,
          canSelectAll: true,
          onSelectAll: (selectedItems): void => handleAddTag(selectedItems),
          onSelect: (selectedItem, allSelectedItems): void => handleAddTag(allSelectedItems),
          menuItems: tagBlockRequestTypeOptions.filter(({ isActive, isDeleted }) => !isDeleted && isActive),
          maxH: '300px',
        }}
      />
    </AutomationsFieldContainer>
  );
};
